import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, Copy, LayoutDashboard, Loader2, Pencil, Plus, Send, Settings, Trash2, Users, X } from 'lucide-react';
import { api } from '@onvice/portal-shell-api';

/** Admin-UI: Seiten-Vorlagen verwalten (Joerg-Wunsch 2026-06-08).
 *  Mandant-Vorlagen voll editierbar; Onvice-System-Vorlagen (tenant_id IS NULL)
 *  read-only, koennen aber als Ausgangspunkt geklont werden. */

interface PageTemplate {
  id: string;
  tenant_id: string | null;
  name: string;
  description: string | null;
  icon: string | null;
  suggested_for_groups: string[];
  sort_order: number;
  updated_at: string;
  is_system: boolean;
}

interface Group {
  id: string;
  name: string;
}

export default function AdminPageTemplatesPage() {
  const [templates, setTemplates] = useState<PageTemplate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [editTarget, setEditTarget] = useState<PageTemplate | null>(null);
  const [cloneTarget, setCloneTarget] = useState<PageTemplate | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PageTemplate | null>(null);
  const [pushTarget, setPushTarget] = useState<PageTemplate | null>(null);

  const load = useCallback(() => {
    api.get<{ success: boolean; data: PageTemplate[] }>('/portal/admin/page-templates')
      .then((r) => { if (r.success) { setTemplates(r.data); setError(null); } })
      .catch(() => setError('Fehler beim Laden'));
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get<{ success: boolean; data: Group[] }>('/portal/admin/adressbuch/groups')
      .then((r) => { if (r.success) setGroups(r.data); })
      .catch(() => { /* fail-soft */ });
  }, []);

  const own = useMemo(() => templates?.filter((t) => !t.is_system) ?? [], [templates]);
  const system = useMemo(() => templates?.filter((t) => t.is_system) ?? [], [templates]);

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <LayoutDashboard size={22} /> Seiten-Vorlagen
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Vorlagen erscheinen im „Neue Seite"-Dialog beim User. Mandant-Vorlagen sind editierbar, onvice-System-Vorlagen nur lesend (per „Klonen" kopierst du sie in den Mandanten).
          </p>
        </div>
        <button
          type="button"
          onClick={() => setNewOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-brand-600 text-primary-foreground hover:bg-brand-500 text-sm"
        >
          <Plus size={14} /> Neue Vorlage
        </button>
      </header>

      {error && (
        <div className="px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/40 text-red-300 text-sm flex items-center gap-2">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      <section>
        <h2 className="text-sm uppercase tracking-wide text-muted-foreground mb-2">Mandant-Vorlagen</h2>
        {!templates ? (
          <div className="flex items-center justify-center py-6"><Loader2 size={20} className="animate-spin text-muted-foreground" /></div>
        ) : own.length === 0 ? (
          <div className="text-sm text-muted-foreground border border-dashed border-default rounded-lg py-6 text-center">
            Noch keine eigenen Vorlagen. Lege oben rechts eine neue an oder klone eine onvice-Vorlage.
          </div>
        ) : (
          <TemplateList items={own} groupsById={groups} onEdit={setEditTarget} onDelete={setDeleteTarget} onPush={setPushTarget} />
        )}
      </section>

      <section>
        <h2 className="text-sm uppercase tracking-wide text-muted-foreground mb-2">onvice-Vorlagen (read-only)</h2>
        {!templates ? null : system.length === 0 ? (
          <div className="text-sm text-muted-foreground">Keine onvice-Vorlagen geseedet.</div>
        ) : (
          <TemplateList items={system} groupsById={groups} readOnly onClone={setCloneTarget} onPush={setPushTarget} />
        )}
      </section>

      {editTarget && (
        <MetadataDialog
          template={editTarget}
          groups={groups}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); load(); }}
        />
      )}

      {newOpen && (
        <NewTemplateDialog
          groups={groups}
          onClose={() => setNewOpen(false)}
          onCreated={() => { setNewOpen(false); load(); }}
        />
      )}

      {cloneTarget && (
        <CloneDialog
          source={cloneTarget}
          onClose={() => setCloneTarget(null)}
          onCloned={() => { setCloneTarget(null); load(); }}
        />
      )}

      {deleteTarget && (
        <DeleteDialog
          template={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => { setDeleteTarget(null); load(); }}
        />
      )}

      {pushTarget && (
        <PushDialog
          template={pushTarget}
          groups={groups}
          onClose={() => setPushTarget(null)}
          onPushed={() => setPushTarget(null)}
        />
      )}
    </div>
  );
}

function TemplateList({ items, groupsById, readOnly, onEdit, onDelete, onClone, onPush }: {
  items: PageTemplate[];
  groupsById: Group[];
  readOnly?: boolean;
  onEdit?: (t: PageTemplate) => void;
  onDelete?: (t: PageTemplate) => void;
  onClone?: (t: PageTemplate) => void;
  onPush?: (t: PageTemplate) => void;
}) {
  const nameOf = (id: string) => groupsById.find((g) => g.id === id)?.name ?? id.slice(0, 6);
  return (
    <ul className="divide-y divide-default rounded-lg border border-default overflow-hidden bg-card">
      {items.map((t) => (
        <li key={t.id} className="flex items-center gap-2 p-3 hover:bg-muted transition-colors">
          <LayoutDashboard size={16} className="text-muted-foreground shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate flex items-center gap-2">
              {t.name}
              {readOnly && <span className="text-[10px] uppercase tracking-wide text-muted-foreground bg-muted px-1.5 py-0.5 rounded">System</span>}
            </div>
            {t.description && <div className="text-xs text-muted-foreground truncate">{t.description}</div>}
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              <Users size={11} className="text-muted-foreground shrink-0" />
              {t.suggested_for_groups.length === 0 ? (
                <span className="text-[11px] text-muted-foreground italic">Keine Gruppen-Empfehlung — nur über Picker oder Push</span>
              ) : (
                t.suggested_for_groups.map((gid) => (
                  <span
                    key={gid}
                    className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] bg-brand-500/10 text-brand-300 border border-brand-500/30"
                    title="Wird beim Erst-Login dieser Gruppe automatisch geseedet"
                  >
                    {nameOf(gid)}
                  </span>
                ))
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => onPush?.(t)}
            className="inline-flex items-center gap-1 px-2 py-1.5 rounded text-xs text-muted-foreground hover:text-brand-300 hover:bg-background min-h-[40px]"
            title="Diese Vorlage an alle Mitglieder einer Gruppe als persönliche Seite anhängen"
          >
            <Send size={14} /> Pushen
          </button>
          {!readOnly ? (
            <>
              <Link
                to={`/admin/page-templates/${t.id}`}
                className="inline-flex items-center gap-1 px-2 py-1.5 rounded text-xs text-muted-foreground hover:text-foreground hover:bg-background min-h-[40px]"
                title="Layout der Vorlage bearbeiten"
              >
                <LayoutDashboard size={14} /> Layout
              </Link>
              <button
                type="button"
                onClick={() => onEdit?.(t)}
                className="p-2 text-muted-foreground hover:text-foreground rounded hover:bg-background min-h-[40px] min-w-[40px] inline-flex items-center justify-center"
                title="Name / Beschreibung / Empfehlung"
              >
                <Pencil size={14} />
              </button>
              <button
                type="button"
                onClick={() => onDelete?.(t)}
                className="p-2 text-muted-foreground hover:text-rose-400 rounded hover:bg-background min-h-[40px] min-w-[40px] inline-flex items-center justify-center"
                title="Vorlage löschen"
              >
                <Trash2 size={14} />
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => onClone?.(t)}
              className="inline-flex items-center gap-1 px-2 py-1.5 rounded text-xs text-muted-foreground hover:text-foreground hover:bg-background min-h-[40px]"
              title="Vorlage in den Mandanten kopieren (dann editierbar)"
            >
              <Copy size={14} /> Klonen
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

function PushDialog({ template, groups, onClose, onPushed }: {
  template: PageTemplate;
  groups: Group[];
  onClose: () => void;
  onPushed: () => void;
}) {
  const [groupId, setGroupId] = useState<string>(template.suggested_for_groups[0] ?? groups[0]?.id ?? '');
  const [includeExisting, setIncludeExisting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ pushed: number; skipped: number; totalUsers: number } | null>(null);

  const groupName = groups.find((g) => g.id === groupId)?.name ?? '';

  const push = async () => {
    if (!groupId) { setError('Bitte eine Gruppe auswählen.'); return; }
    setBusy(true); setError(null);
    try {
      const r = await api.post<{ success: boolean; data?: { pushed: number; skipped: number; totalUsers: number }; error?: string }>(
        `/portal/admin/page-templates/${template.id}/push`,
        { groupId, includeExisting },
      );
      if (r.success && r.data) setResult(r.data);
      else setError(r.error ?? 'Push fehlgeschlagen');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`„${template.name}" an Gruppe pushen`} onClose={onClose}>
      {result ? (
        <>
          <div className="px-4 py-3 rounded-lg bg-brand-500/10 border border-brand-500/40 text-brand-200 text-sm space-y-1">
            <div><strong>{result.pushed}</strong> Seite{result.pushed === 1 ? '' : 'n'} angelegt</div>
            {result.skipped > 0 && <div className="text-brand-300/80">{result.skipped} User übersprungen (Seite war bereits vorhanden)</div>}
            <div className="text-brand-300/80">Gruppe „{groupName}" hat {result.totalUsers} Mitglied{result.totalUsers === 1 ? '' : 'er'} mit User-Account</div>
          </div>
          <Footer>
            <button onClick={() => { setResult(null); onPushed(); onClose(); }} className="px-4 py-2 rounded-lg bg-brand-600 text-primary-foreground hover:bg-brand-500 text-sm">Schließen</button>
          </Footer>
        </>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            Die Vorlage wird als persönliche Seite ans Ende der Seiten-Liste jedes Gruppen-Mitglieds angehängt. User können sie danach umbenennen, anpassen oder löschen — die Vorlage selbst bleibt unverändert.
          </p>
          <Field label="Ziel-Gruppe">
            {groups.length === 0 ? (
              <div className="text-sm text-muted-foreground">Keine Gruppen vorhanden.</div>
            ) : (
              <select value={groupId} onChange={(e) => setGroupId(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-default bg-card text-foreground">
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            )}
          </Field>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeExisting}
              onChange={(e) => setIncludeExisting(e.target.checked)}
              className="mt-0.5"
            />
            <span className="text-sm">
              <span className="font-medium">Auch bei Usern, die die Seite schon haben, erneut anhängen</span>
              <span className="block text-[11px] text-muted-foreground">Standard: bestehende Empfänger werden übersprungen. Aktivieren, wenn du den aktuellen Stand der Vorlage als zusätzliche neue Seite ausrollen willst.</span>
            </span>
          </label>
          {error && <div className="text-sm text-red-500">{error}</div>}
          <Footer>
            <button onClick={onClose} disabled={busy} className="px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground">Abbrechen</button>
            <button onClick={push} disabled={busy || !groupId} className="px-4 py-2 rounded-lg bg-brand-600 text-primary-foreground hover:bg-brand-500 text-sm disabled:opacity-50 inline-flex items-center gap-1">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Pushen
            </button>
          </Footer>
        </>
      )}
    </Modal>
  );
}

function MetadataDialog({ template, groups, onClose, onSaved }: {
  template: PageTemplate;
  groups: Group[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(template.name);
  const [description, setDescription] = useState(template.description ?? '');
  const [icon, setIcon] = useState(template.icon ?? 'LayoutDashboard');
  const [picked, setPicked] = useState<Set<string>>(new Set(template.suggested_for_groups));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (id: string) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.patch<{ success: boolean; error?: string }>(`/portal/admin/page-templates/${template.id}`, {
        name: name.trim(),
        description: description.trim() || null,
        icon: icon.trim() || null,
        suggestedForGroups: Array.from(picked),
      });
      if (r.success) onSaved(); else setError(r.error ?? 'Speichern fehlgeschlagen');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Vorlage bearbeiten" onClose={onClose}>
      <Field label="Name">
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-default bg-card text-foreground" />
      </Field>
      <Field label="Beschreibung (optional)">
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="w-full px-3 py-2 rounded-lg border border-default bg-card text-foreground" />
      </Field>
      <Field label="Icon (lucide-Name, z.B. LayoutDashboard)">
        <input type="text" value={icon} onChange={(e) => setIcon(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-default bg-card text-foreground" />
      </Field>
      <Field label="Empfohlen für Gruppen">
        <div className="max-h-48 overflow-y-auto border border-default rounded-lg divide-y divide-default">
          {groups.length === 0 ? (
            <div className="text-sm text-muted-foreground p-3">Keine Gruppen vorhanden.</div>
          ) : groups.map((g) => (
            <label key={g.id} className="flex items-center gap-2 p-2 hover:bg-muted cursor-pointer">
              <input type="checkbox" checked={picked.has(g.id)} onChange={() => toggle(g.id)} />
              <span className="text-sm">{g.name}</span>
            </label>
          ))}
        </div>
        <div className="text-[11px] text-muted-foreground mt-1">Beim Erst-Login werden Vorlagen, die mit mind. einer Gruppe des Users übereinstimmen, automatisch in seine Pages geseedet.</div>
      </Field>
      {error && <div className="text-sm text-red-500">{error}</div>}
      <Footer>
        <button onClick={onClose} disabled={busy} className="px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground">Abbrechen</button>
        <button onClick={save} disabled={busy || !name.trim()} className="px-4 py-2 rounded-lg bg-brand-600 text-primary-foreground hover:bg-brand-500 text-sm disabled:opacity-50">
          {busy ? <Loader2 size={14} className="inline animate-spin mr-1" /> : null}
          Speichern
        </button>
      </Footer>
    </Modal>
  );
}

function NewTemplateDialog({ groups, onClose, onCreated }: {
  groups: Group[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (id: string) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const create = async () => {
    setBusy(true); setError(null);
    try {
      const r = await api.post<{ success: boolean; data: { id: string }; error?: string }>('/portal/admin/page-templates', {
        name: name.trim(),
        description: description.trim() || null,
        icon: 'LayoutDashboard',
        suggestedForGroups: Array.from(picked),
      });
      if (r.success) onCreated(); else setError(r.error ?? 'Anlegen fehlgeschlagen');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Neue Vorlage" onClose={onClose}>
      <Field label="Name">
        <input type="text" value={name} autoFocus onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-default bg-card text-foreground" placeholder="z.B. Meister-Tagesüberblick" />
      </Field>
      <Field label="Beschreibung (optional)">
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="w-full px-3 py-2 rounded-lg border border-default bg-card text-foreground" />
      </Field>
      <Field label="Empfohlen für Gruppen">
        <div className="max-h-48 overflow-y-auto border border-default rounded-lg divide-y divide-default">
          {groups.length === 0 ? (
            <div className="text-sm text-muted-foreground p-3">Keine Gruppen vorhanden.</div>
          ) : groups.map((g) => (
            <label key={g.id} className="flex items-center gap-2 p-2 hover:bg-muted cursor-pointer">
              <input type="checkbox" checked={picked.has(g.id)} onChange={() => toggle(g.id)} />
              <span className="text-sm">{g.name}</span>
            </label>
          ))}
        </div>
      </Field>
      <div className="text-xs text-muted-foreground">
        Die Vorlage wird leer angelegt. Layout fügst du nach dem Anlegen über „Layout" hinzu.
      </div>
      {error && <div className="text-sm text-red-500">{error}</div>}
      <Footer>
        <button onClick={onClose} disabled={busy} className="px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground">Abbrechen</button>
        <button onClick={create} disabled={busy || !name.trim()} className="px-4 py-2 rounded-lg bg-brand-600 text-primary-foreground hover:bg-brand-500 text-sm disabled:opacity-50">
          {busy ? <Loader2 size={14} className="inline animate-spin mr-1" /> : null}
          Anlegen
        </button>
      </Footer>
    </Modal>
  );
}

function CloneDialog({ source, onClose, onCloned }: {
  source: PageTemplate;
  onClose: () => void;
  onCloned: () => void;
}) {
  const [name, setName] = useState(`${source.name} (Kopie)`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clone = async () => {
    setBusy(true); setError(null);
    try {
      const r = await api.post<{ success: boolean; data: { id: string }; error?: string }>('/portal/admin/page-templates', {
        name: name.trim(),
        cloneFromId: source.id,
        suggestedForGroups: source.suggested_for_groups,
      });
      if (r.success) onCloned(); else setError(r.error ?? 'Klonen fehlgeschlagen');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`„${source.name}" klonen`} onClose={onClose}>
      <p className="text-sm text-muted-foreground">
        Layout, Icon, Beschreibung und Gruppen-Empfehlung werden übernommen. Die Kopie gehört dem Mandanten und ist anschließend voll editierbar.
      </p>
      <Field label="Name der Kopie">
        <input type="text" value={name} autoFocus onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-default bg-card text-foreground" />
      </Field>
      {error && <div className="text-sm text-red-500">{error}</div>}
      <Footer>
        <button onClick={onClose} disabled={busy} className="px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground">Abbrechen</button>
        <button onClick={clone} disabled={busy || !name.trim()} className="px-4 py-2 rounded-lg bg-brand-600 text-primary-foreground hover:bg-brand-500 text-sm disabled:opacity-50">
          {busy ? <Loader2 size={14} className="inline animate-spin mr-1" /> : null}
          Klonen
        </button>
      </Footer>
    </Modal>
  );
}

function DeleteDialog({ template, onClose, onDeleted }: {
  template: PageTemplate;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const del = async () => {
    setBusy(true); setError(null);
    try {
      const r = await api.delete<{ success: boolean; error?: string }>(`/portal/admin/page-templates/${template.id}`);
      if (r.success) onDeleted(); else setError(r.error ?? 'Löschen fehlgeschlagen');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Vorlage löschen?" onClose={onClose}>
      <p className="text-sm">
        „{template.name}" wird endgültig gelöscht. Existierende persönliche Seiten von Usern, die aus dieser Vorlage entstanden sind, bleiben bestehen — aber „Auf Vorlage zurücksetzen" funktioniert für sie danach nicht mehr.
      </p>
      {error && <div className="text-sm text-red-500">{error}</div>}
      <Footer>
        <button onClick={onClose} disabled={busy} className="px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground">Abbrechen</button>
        <button onClick={del} disabled={busy} className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-500 text-sm disabled:opacity-50 inline-flex items-center gap-1">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
          Endgültig löschen
        </button>
      </Footer>
    </Modal>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-card border border-default rounded-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between px-5 py-3 border-b border-default">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Schliessen">
            <X size={18} />
          </button>
        </header>
        <div className="p-5 space-y-4 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-sm font-medium mb-1">{label}</div>
      {children}
    </label>
  );
}

function Footer({ children }: { children: React.ReactNode }) {
  return <div className="flex justify-end items-center gap-2 pt-2">{children}</div>;
}

// Settings ist importiert aber nicht direkt verwendet — lint-clean halten:
void Settings;
